import { Controller } from "@hotwired/stimulus"

const ASSISTANT_TYPING_SPINNER =
  '.ai-message-body[data-ai-message-role="assistant"] .ai-assistant-typing-indicator'

const COURSE_BUILDER_NEW_COURSE_DRAFT_PREFIX = "courseBuilderNewCourseDraft:"

function newCourseDraftStorageKey(selectionBasketId) {
  return `${COURSE_BUILDER_NEW_COURSE_DRAFT_PREFIX}${selectionBasketId}`
}

const SKIP_FORM_FIELD_NAMES = new Set([
  "authenticity_token",
  "utf8",
  "_method",
  "document_set[document_ids][]"
])

export default class extends Controller {
  static targets = [
    "modal",
    "addToCourseBtn",
    "additionalTime",
    "footerCompose",
    "submitModal",
    "cases",
    "promptInput",
    "promptSubmit",
  ]

  connect() {
    if (this.hasModalTarget) {
      this.initialChatHtml = this.modalTarget.querySelector("#course_builder_ai_chat")?.innerHTML || ""
      this.initialFooterComposeHtml = this.hasFooterComposeTarget ? this.footerComposeTarget.innerHTML : ""
      this.initialAdditionalTimeHtml = this.hasAdditionalTimeTarget ? this.additionalTimeTarget.innerHTML : ""
      this.initialSidebarHtml = this.modalTarget.querySelector(".course-builder-ai-modal__sidebar")?.innerHTML || ""
    }
    this._focusManagementActive = false
    if (this.isNewCourseRecordPage()) {
      this.scheduleRestoreNewCourseFormDraft()
      this.bindClearNewCourseDraftOnSubmit()
    }
    this._promptSubmitInFlight = false
    this._defaultPromptPlaceholder = ""
    this._chatObserver = null
    this._chatSyncDebounce = null
    this._onPromptTurboSubmitStart = (e) => this.promptTurboSubmitStart(e)
    this._onPromptTurboSubmitEnd = (e) => this.promptTurboSubmitEnd(e)
    this._onPromptAfterStream = () => this.syncPromptSubmitState()
    document.addEventListener("turbo:submit-start", this._onPromptTurboSubmitStart)
    document.addEventListener("turbo:submit-end", this._onPromptTurboSubmitEnd)
    document.addEventListener("turbo:fetch-request-error", this._onPromptTurboSubmitEnd)
    document.addEventListener("turbo:after-stream-render", this._onPromptAfterStream)

    this._bindCourseBuilderChatObserver()
    this.syncPromptSubmitState()
  }

  promptTurboSubmitStart(event) {
    const form = this._formFromTurboSubmitEvent(event)
    if (!this._courseBuilderAiAssistantFormLive(form)) return
    this._promptSubmitInFlight = true
    this.syncPromptSubmitState()
  }

  promptTurboSubmitEnd(event) {
    const form = this._formFromTurboSubmitEvent(event)
    if (!this._courseBuilderAiAssistantFormDetached(form)) return
    this._promptSubmitInFlight = false
    this.syncPromptSubmitState()
    this._schedulePromptFollowUpSync()
  }

  _formFromTurboSubmitEvent(event) {
    return event.detail?.formSubmission?.formElement || (event.target?.tagName === "FORM" ? event.target : null)
  }

  _courseBuilderAiAssistantFormDetached(form) {
    if (!form || form.tagName !== "FORM" || !form.action?.includes("/ai_assistant/")) return false
    return Boolean(form.querySelector('input[name="from_course_builder"]'))
  }

  _courseBuilderAiAssistantFormLive(form) {
    return (
      this._courseBuilderAiAssistantFormDetached(form) &&
      this.hasModalTarget &&
      this.modalTarget.contains(form)
    )
  }

  _promptAwaitingAssistantResponse() {
    const chat = this.hasModalTarget ? this.modalTarget.querySelector("#course_builder_ai_chat") : null
    return Boolean(chat?.querySelector(ASSISTANT_TYPING_SPINNER))
  }

  _courseBuilderChatRoot() {
    return this.hasModalTarget ? this.modalTarget.querySelector("#course_builder_ai_chat") : null
  }

  _bindCourseBuilderChatObserver() {
    this._chatObserver?.disconnect()
    const chat = this._courseBuilderChatRoot()
    if (!chat) return
    this._chatObserver = new MutationObserver(() => this._debouncedChatSync())
    this._chatObserver.observe(chat, { childList: true, subtree: true })
  }

  _debouncedChatSync() {
    clearTimeout(this._chatSyncDebounce)
    this._chatSyncDebounce = setTimeout(() => {
      this._chatSyncDebounce = null
      this.syncPromptSubmitState()
    }, 100)
  }

  _schedulePromptFollowUpSync() {
    requestAnimationFrame(() => {
      this.syncPromptSubmitState()
      requestAnimationFrame(() => this.syncPromptSubmitState())
    })
  }

  syncPromptSubmitState() {
    if (!this.hasPromptSubmitTarget) return
    const empty =
      !this.hasPromptInputTarget || this.promptInputTarget.value.trim() === ""
    const awaiting =
      Boolean(this._promptSubmitInFlight) || this._promptAwaitingAssistantResponse()

    if (this.hasPromptInputTarget) {
      this.promptInputTarget.disabled = awaiting
      this.promptInputTarget.placeholder = awaiting
        ? "AI is responding..."
        : this._defaultPromptPlaceholder || "Type a message"
    }

    this.promptSubmitTarget.disabled = empty || awaiting
  }

  promptInputTargetConnected() {
    if (!this._defaultPromptPlaceholder && this.promptInputTarget.placeholder) {
      this._defaultPromptPlaceholder = this.promptInputTarget.placeholder
    }
    this._bindCourseBuilderChatObserver()
    this.syncPromptSubmitState()
  }

  promptSubmitTargetConnected() {
    this.syncPromptSubmitState()
  }

  disconnect() {
    this.stopFocusManagement()
    this._chatObserver?.disconnect()
    this._chatObserver = null
    clearTimeout(this._chatSyncDebounce)
    this._chatSyncDebounce = null
    if (this._onPromptTurboSubmitStart) {
      document.removeEventListener("turbo:submit-start", this._onPromptTurboSubmitStart)
      document.removeEventListener("turbo:submit-end", this._onPromptTurboSubmitEnd)
      document.removeEventListener("turbo:fetch-request-error", this._onPromptTurboSubmitEnd)
      document.removeEventListener("turbo:after-stream-render", this._onPromptAfterStream)
    }
  }

  disableOrEnableScrollOnBody(disable = true) {
    document.body.style.overflow = disable ? "hidden" : ""
  }

  open() {
    this.openerElement = document.activeElement
    this.closeSubmitModal(false)
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("modal-open")
    // Disable scroll on body
    this.disableOrEnableScrollOnBody(true)
    this.modalTarget.querySelector('[aria-label="Close AI Assistant"]')?.focus()
    this.startFocusManagement()
    this._bindCourseBuilderChatObserver()
    this.syncPromptSubmitState()
  }

  close() {
    // Enable scroll on body
    this.disableOrEnableScrollOnBody(false)
    this.closeSubmitModal()
    this.resetAiSession()
    this.modalTarget.classList.add("hidden")
    this.finalizeModalClosed()
  }

  openSubmitModal() {
    if (this.addToCourseBtnTarget.disabled) return

    this.renderSubmitModalItems()
    this.modalTarget.classList.add("hidden")
    this.submitModalTarget.classList.remove("hidden")
    this.submitModalTarget.classList.add("open")
    this.retargetFocusTrap()
    const focusable = this.getFocusableElements(this.submitModalTarget)
    if (focusable.length) focusable[0].focus()
  }

  closeSubmitModal(restoreAiModal = true) {
    if (!this.hasSubmitModalTarget) return

    this.submitModalTarget.classList.remove("open")
    this.submitModalTarget.classList.add("hidden")
    // Enable scroll on body
    this.disableOrEnableScrollOnBody(false)
    if (restoreAiModal) {
      this.modalTarget.classList.remove("hidden")
      this.retargetFocusTrap()
      this.modalTarget.querySelector('[aria-label="Close AI Assistant"]')?.focus()
    }
  }

  finalizeModalClosed() {
    this.stopFocusManagement()
    document.body.classList.remove("modal-open")
    // Enable scroll on body
    this.disableOrEnableScrollOnBody(false)
    if (this.openerElement && typeof this.openerElement.focus === "function") {
      try {
        this.openerElement.focus()
      } catch (_) {
        /* ignore */
      }
    }
    this.openerElement = null
  }

  activeModalLayer() {
    if (this.hasSubmitModalTarget && this.submitModalTarget.classList.contains("open")) {
      return this.submitModalTarget
    }
    if (this.modalTarget && !this.modalTarget.classList.contains("hidden")) {
      return this.modalTarget
    }
    return null
  }

  getFocusableElements(container) {
    if (!container) return []

    return Array.from(
      container.querySelectorAll(
        "a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex='-1'])"
      )
    ).filter((el) => {
      const style = window.getComputedStyle(el)
      if (style.visibility === "hidden" || style.display === "none") return false
      const visibleBox = el.offsetParent !== null || el.getClientRects().length > 0
      return el.tabIndex >= 0 && !el.hasAttribute("disabled") && visibleBox
    })
  }

  startFocusManagement() {
    if (this._focusManagementActive) this.stopFocusManagement()
    this._focusManagementActive = true

    this._focusinHandler = (e) => {
      const layer = this.activeModalLayer()
      if (!layer || layer.contains(e.target)) return
      const focusable = this.getFocusableElements(layer)
      if (focusable.length) focusable[0].focus()
    }
    document.addEventListener("focusin", this._focusinHandler)

    this._mousedownHandler = (e) => {
      const layer = this.activeModalLayer()
      if (!layer || layer.contains(e.target)) return
      e.preventDefault()
      const focusable = this.getFocusableElements(layer)
      if (focusable.length) focusable[0].focus()
    }
    document.addEventListener("mousedown", this._mousedownHandler)

    this._escapeHandler = (e) => {
      if (e.key !== "Escape") return
      const layer = this.activeModalLayer()
      if (!layer) return
      e.preventDefault()
      if (layer === this.submitModalTarget) {
        this.closeSubmitModal()
      } else {
        this.close()
      }
    }
    document.addEventListener("keydown", this._escapeHandler)

    this.retargetFocusTrap()
  }

  retargetFocusTrap() {
    this.releaseTabTrap()
    const layer = this.activeModalLayer()
    if (!layer || !this._focusManagementActive) return

    const focusable = this.getFocusableElements(layer)
    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    this._modalTabHandler = (event) => {
      if (event.key !== "Tab") return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    this._tabTrapContainer = layer
    layer.addEventListener("keydown", this._modalTabHandler)
  }

  releaseTabTrap() {
    if (this._modalTabHandler && this._tabTrapContainer) {
      this._tabTrapContainer.removeEventListener("keydown", this._modalTabHandler)
    }
    this._modalTabHandler = null
    this._tabTrapContainer = null
  }

  stopFocusManagement() {
    if (!this._focusManagementActive) return

    document.removeEventListener("focusin", this._focusinHandler)
    document.removeEventListener("mousedown", this._mousedownHandler)
    document.removeEventListener("keydown", this._escapeHandler)
    this._focusinHandler = null
    this._mousedownHandler = null
    this._escapeHandler = null

    this.releaseTabTrap()
    this._focusManagementActive = false
  }

  async confirmAdd() {
    const basketId = this.modalTarget.dataset.selectionBasketId
    const documentSetId = this.modalTarget.dataset.documentSetId
    const newRecord = this.modalTarget.dataset.newRecord === "true"

    if (newRecord) {
      const currentIds = Array.from(document.querySelectorAll('input[name="document_set[document_ids][]"]'))
        .map((input) => input.value?.trim())
        .filter(Boolean)
        .map(Number)
      const basketContainer = this.modalTarget.querySelector("#course_builder_ai_basket")
      const threadDocIds = basketContainer?.dataset.threadDocumentIds
        ? JSON.parse(basketContainer.dataset.threadDocumentIds)
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id))
        : []
      const basketIds = basketContainer
        ? Array.from(basketContainer.querySelectorAll(".course-builder-ai-basket-item"))
            .map((el) => parseInt(el.dataset.selectableId, 10))
            .filter((id) => !Number.isNaN(id))
        : []
      const removedFromBasket = threadDocIds.filter(
        (id) => currentIds.includes(id) && !basketIds.includes(id)
      )
      const allIds = [...new Set([...currentIds, ...basketIds])].filter(
        (id) => !removedFromBasket.includes(id)
      )
      const params = new URLSearchParams()
      allIds.forEach((id) => params.append("document_ids[]", id))
      params.set("selection_basket_id", basketId)
      this.persistNewCourseFormDraft(basketId)
      const newUrl = `${window.location.pathname}?${params.toString()}`
      window.location.assign(newUrl)
      return
    }

    const url = this.modalTarget.dataset.addAiSelectionUrl
    if (!url) return

    const basketContainer = this.modalTarget.querySelector("#course_builder_ai_basket")
    const sidebarDocIds = basketContainer
      ? Array.from(basketContainer.querySelectorAll(".course-builder-ai-basket-item"))
          .map((el) => parseInt(el.dataset.selectableId, 10))
          .filter((id) => !Number.isNaN(id))
      : []
    const removeDocIds = this.pendingRemoveIdsFromModal()

    const formData = new FormData()
    formData.append("authenticity_token", document.querySelector('meta[name="csrf-token"]')?.content)
    formData.append("document_set_id", documentSetId || "")
    formData.append("selection_basket_id", basketId)
    const threadId = this.threadIdFromModal()
    if (threadId) formData.append("thread_id", threadId)
    sidebarDocIds.forEach((id) => formData.append("document_ids[]", id))
    removeDocIds.forEach((id) => formData.append("remove_document_ids[]", id))

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(url, {
      method: "POST",
      body: formData,
      redirect: "manual",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken || ""
      }
    })
    if (response.type === "opaqueredirect" || (response.status >= 300 && response.status < 400)) {
      const location = response.headers.get("Location")
      if (location) {
        window.location.assign(location)
      }
      return
    }
    if (!response.ok) {
      await response.text()
      return
    }
    const html = await response.text()
    Turbo.renderStreamMessage(html)
    this.closeSubmitModal()
    this.resetAiSession()
    this.modalTarget.classList.add("hidden")
    this.finalizeModalClosed()
  }

  resetAiSession() {
    const chat = this.modalTarget.querySelector("#course_builder_ai_chat")
    if (chat) chat.innerHTML = this.initialChatHtml

    if (this.hasFooterComposeTarget) this.footerComposeTarget.innerHTML = this.initialFooterComposeHtml

    if (this.hasAdditionalTimeTarget) {
      this.additionalTimeTarget.innerHTML = this.initialAdditionalTimeHtml || ""
    }

    const sidebar = this.modalTarget.querySelector(".course-builder-ai-modal__sidebar")
    if (sidebar) sidebar.innerHTML = this.initialSidebarHtml

    const feedback = this.modalTarget.querySelector("#thread_feedback")
    if (feedback) {
      feedback.innerHTML = ""
    }
    this._bindCourseBuilderChatObserver()
    this.syncPromptSubmitState()
  }

  threadIdFromModal() {
    const hidden = this.modalTarget.querySelector('#thread_message_form input[name="thread_id"]')
    if (hidden?.value) return hidden.value
    const form = this.modalTarget.querySelector("#thread_message_form form")
    if (!form?.action) return ""
    const match = form.action.match(/\/ai_assistant\/threads\/(\d+)/)
    return match ? match[1] : ""
  }

  pendingRemoveIdsFromModal() {
    const forms = Array.from(
      this.modalTarget.querySelectorAll('form[action*="course_builder_ai_unstage_remove_document"]')
    )

    return forms
      .map((form) => parseInt(form.querySelector('input[name="document_id"]')?.value, 10))
      .filter((id) => !Number.isNaN(id))
  }

  renderSubmitModalItems() {
    if (!this.hasCasesTarget) return

    const basketItems = Array.from(
      this.modalTarget.querySelectorAll(".course-builder-ai-basket-item__name")
    )
      .map((el) => el.textContent?.trim())
      .filter(Boolean)

    this.casesTarget.innerHTML = basketItems.map((item) => `<p>${item}</p>`).join("")
  }

  isNewCourseRecordPage() {
    return this.hasModalTarget && this.modalTarget.dataset.newRecord === "true"
  }

  selectionBasketIdForDraft() {
    if (this.hasModalTarget && this.modalTarget.dataset.selectionBasketId) {
      return this.modalTarget.dataset.selectionBasketId
    }
    const hidden = this.element.querySelector('#course_builder_form input[name="selection_basket_id"]')
    return hidden?.value?.trim() || ""
  }

  persistNewCourseFormDraft(selectionBasketId) {
    if (!selectionBasketId) return
    const formRoot = this.element.querySelector("#course_builder_form")
    if (!formRoot) return
    const payload = this.serializeCourseBuilderForm(formRoot)
    try {
      sessionStorage.setItem(newCourseDraftStorageKey(selectionBasketId), JSON.stringify(payload))
    } catch (_) {
      /* ignore quota / private mode */
    }
  }

  serializeCourseBuilderForm(formRoot) {
    const data = {}
    formRoot.querySelectorAll("input, select, textarea").forEach((el) => {
      if (!el.name || el.disabled || SKIP_FORM_FIELD_NAMES.has(el.name)) return
      if (el.type === "file" || el.type === "submit" || el.type === "button" || el.type === "image") return

      if (el.type === "checkbox") {
        if (!Object.prototype.hasOwnProperty.call(data, el.name)) data[el.name] = []
        if (el.checked) data[el.name].push(el.value)
        return
      }

      if (el.type === "radio") {
        if (el.checked) data[el.name] = el.value
        return
      }

      if (el.tagName === "SELECT" && el.multiple) {
        data[el.name] = Array.from(el.selectedOptions).map((o) => o.value)
        return
      }

      data[el.name] = el.value
    })
    return data
  }

  scheduleRestoreNewCourseFormDraft() {
    const basketId = this.selectionBasketIdForDraft()
    if (!basketId) return
    let raw
    try {
      raw = sessionStorage.getItem(newCourseDraftStorageKey(basketId))
    } catch (_) {
      return
    }
    if (!raw) return

    const run = () => {
      const formRoot = this.element.querySelector("#course_builder_form")
      if (!formRoot) return
      const form = formRoot.querySelector("form")
      if (!form) return
      let draft
      try {
        draft = JSON.parse(raw)
      } catch (_) {
        return
      }
      this.applyDraftToForm(form, draft)
    }

    setTimeout(run, 0)
  }

  applyDraftToForm(form, draft) {
    Object.entries(draft).forEach(([name, stored]) => {
      const control = form.elements[name]
      if (!control) return

      if (control.length !== undefined && control.length > 0 && control[0]?.type === "radio") {
        Array.from(control).forEach((r) => {
          r.checked = r.value === String(stored)
        })
        return
      }

      if (control.length !== undefined && control.length > 0 && control[0]?.type === "checkbox") {
        const values = Array.isArray(stored) ? stored.map(String) : []
        Array.from(control).forEach((cb) => {
          cb.checked = values.includes(cb.value)
        })
        return
      }

      if (control.type === "checkbox") {
        control.checked = Boolean(stored)
        return
      }

      if (control.type === "radio") {
        control.checked = control.value === String(stored)
        return
      }

      if (control.tagName === "SELECT" && control.multiple) {
        const values = Array.isArray(stored) ? stored.map(String) : []
        Array.from(control.options).forEach((opt) => {
          opt.selected = values.includes(opt.value)
        })
        control.dispatchEvent(new Event("change", { bubbles: true }))
        return
      }

      if (control.tagName === "SELECT") {
        control.value = stored ?? ""
        control.dispatchEvent(new Event("change", { bubbles: true }))
        return
      }

      control.value = stored ?? ""
      control.dispatchEvent(new Event("input", { bubbles: true }))
      control.dispatchEvent(new Event("change", { bubbles: true }))
      control.dispatchEvent(new CustomEvent("datepicker:changed", { bubbles: true }))
    })
  }

  bindClearNewCourseDraftOnSubmit() {
    const form = this.element.querySelector("#course_builder_form form")
    if (!form || form.dataset.courseBuilderDraftClearBound === "true") return
    form.dataset.courseBuilderDraftClearBound = "true"
    form.addEventListener("submit", () => {
      const id = this.selectionBasketIdForDraft()
      if (!id) return
      try {
        sessionStorage.removeItem(newCourseDraftStorageKey(id))
      } catch (_) {
        /* ignore */
      }
    })
  }
}
